/**
 * DataSection — zero-blank-state wrapper.
 *
 * Rule: never show a blank section. Always show one of:
 *   • Children (data)
 *   • LoadingSkeleton
 *   • ErrorCard  (with optional retry)
 *   • EmptyCard
 *
 * Usage:
 *   <DataSection title="Positions" loading={loading} error={error} empty={positions.length === 0} onRetry={fetch}>
 *     <PositionTable ... />
 *   </DataSection>
 */
import type { ReactNode } from 'react'
import { AlertCircle, RefreshCw, Database } from 'lucide-react'

// ── Loading skeleton ────────────────────────────────────────────────────────

function LoadingSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2.5 animate-pulse" aria-busy="true" aria-label="Loading…">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="h-4 rounded-md bg-surface-hover"
          style={{ width: `${65 + (i % 3) * 12}%` }}
        />
      ))}
    </div>
  )
}

// ── Error card ──────────────────────────────────────────────────────────────

function ErrorCard({
  message,
  onRetry,
}: {
  message: string
  onRetry?: () => void
}) {
  return (
    <div
      role="alert"
      className="flex items-start gap-3 p-4 rounded-xl bg-rose/10 border border-rose/20 text-sm"
    >
      <AlertCircle size={16} className="text-rose-light shrink-0 mt-0.5" aria-hidden />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-rose-light">Failed to load data</p>
        <p className="text-text-secondary mt-0.5 text-xs">{message}</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="mt-2 flex items-center gap-1.5 text-xs text-blue-light hover:underline"
          >
            <RefreshCw size={11} aria-hidden />
            Try again
          </button>
        )}
      </div>
    </div>
  )
}

// ── Empty card ──────────────────────────────────────────────────────────────

function EmptyCard({ message = 'No data available yet' }: { message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
      <Database size={28} className="text-text-muted opacity-30" aria-hidden />
      <p className="text-sm text-text-muted">{message}</p>
    </div>
  )
}

// ── DataSection ─────────────────────────────────────────────────────────────

interface DataSectionProps {
  /** Optional section title rendered above the content. */
  title?: string
  /** Show skeleton while true. */
  loading?: boolean
  /** Show error card when non-null/non-empty. */
  error?: string | null
  /** Show empty card when true. */
  empty?: boolean
  /** Message shown in the empty state. */
  emptyMessage?: string
  /** Called when the user clicks "Try again". */
  onRetry?: () => void
  /** Number of skeleton rows to render. */
  skeletonRows?: number
  children: ReactNode
}

export default function DataSection({
  title,
  loading = false,
  error,
  empty = false,
  emptyMessage = 'No data available yet',
  onRetry,
  skeletonRows = 3,
  children,
}: DataSectionProps) {
  return (
    <div className="w-full">
      {title && (
        <h3 className="text-sm font-semibold text-text-primary mb-3">{title}</h3>
      )}

      {loading ? (
        <LoadingSkeleton rows={skeletonRows} />
      ) : error ? (
        <ErrorCard message={error} onRetry={onRetry} />
      ) : empty ? (
        <EmptyCard message={emptyMessage} />
      ) : (
        children
      )}
    </div>
  )
}

// Re-export primitives so callers can use them directly if needed
export { LoadingSkeleton, ErrorCard, EmptyCard }
